// Faithful port of eb-ui `packages/eds/eds-share-box/src/FacebookMessengerShare.jsx`.
// NB the mobile deep link calls `_getEventUrl` WITHOUT utmOptions (only the aff
// code) — that's the eb-ui source verbatim, preserved for byte parity.
import React from 'react';
import { formatUrl } from 'url-lib';
import { IconButton } from '@eventbrite/legacy-eds.icon-button';
import { LogoMessenger } from '@eventbrite/legacy-eds.iconography';
import { gettext } from '../../i18n/gettext';
import { FACEBOOK_MESSENGER_AFFILIATED_CODE } from './constants';
import { getSdk, initApplication, openUI } from './facebookUtils';
import { openUrl } from './utils';
import type { BaseShareProps, FacebookOptions, UtmOptions } from './types';

const _getEventUrl = (
    eventUrl: string,
    affiliateChannel: string | undefined,
    utmOptions?: UtmOptions,
) =>
    formatUrl(eventUrl, {
        aff:
            (affiliateChannel &&
                FACEBOOK_MESSENGER_AFFILIATED_CODE[affiliateChannel]) ||
            FACEBOOK_MESSENGER_AFFILIATED_CODE['default'],
        ...utmOptions,
    });

const _getFacebookUrl = (
    eventUrl: string,
    affiliateChannel: string | undefined,
    appId: string,
) =>
    formatUrl('fb-messenger://share', {
        link: _getEventUrl(eventUrl, affiliateChannel),
        app_id: appId,
    });

export interface FacebookMessengerShareProps extends BaseShareProps {
    facebookOptions: FacebookOptions;
}

export default class FacebookMessengerShare extends React.Component<FacebookMessengerShareProps> {
    componentDidMount() {
        const { locale, ...options } = this.props.facebookOptions;

        // get facebook-sdk as soon as the component is mounted
        getSdk(locale).then(() => initApplication(options));
    }

    _handleButtonClick() {
        const {
            affiliateChannel,
            eventName,
            eventUrl,
            facebookOptions: { appId, locale },
            isMobile,
            onClick,
            utmOptions,
        } = this.props;

        if (onClick) {
            onClick();
        }

        if (isMobile) {
            openUrl(_getFacebookUrl(eventUrl, affiliateChannel, appId));
        } else {
            const description = gettext(
                'Check out "%(eventName)s" @Eventbrite',
                {
                    eventName,
                },
            );
            const options = {
                description,
                method: 'send',
                display: 'popup',
                link: _getEventUrl(eventUrl, affiliateChannel, utmOptions),
            };

            // verify facebook-sdk is loaded and then open facebook share dialog
            getSdk(locale).then(() => openUI(options));
        }
    }

    render() {
        return (
            <span
                className="eds-l-pad-right-1"
                data-spec="facebook-messenger-share-box-container"
                data-heap-id={this.props.dataHeapId}
                data-share-method={this.props.shareMethod}
                data-page-area={this.props.dataPageArea}
                data-event-id={this.props.eventId}
            >
                <IconButton
                    iconType={<LogoMessenger />}
                    title={gettext('Share on Facebook Messenger')}
                    onClick={this._handleButtonClick.bind(this)}
                />
            </span>
        );
    }
}
